// SubscribeModal — smart opt-in capture.
// Appears once per visitor (per 7-day cookie) after either:
//   • 25 seconds on the page, OR
//   • exit-intent (mouse moves above the top of the viewport)
// whichever comes first. Submits to the same /api/leads endpoint as the in-page
// "Send Me the Details" form, so subscribers flow into the same nurture funnel.
import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { track } from "@/lib/tracker";
import { useSiteContent } from "@/lib/siteContent";
import { getStoredReferrer } from "@/lib/referrer";

const STORAGE_KEY = "fim_subscribe_seen";
const SUPPRESS_DAYS = 7;
const SHOW_DELAY_MS = 25_000;

function recentlyShown() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (!ts) return false;
    return Date.now() - ts < SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markShown() {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
}

export default function SubscribeModal() {
  const sc = useSiteContent();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (recentlyShown()) return;

    const trigger = (reason) => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      markShown();
      setOpen(true);
      track("subscribe_modal_shown", { reason });
    };

    const timer = setTimeout(() => trigger("dwell"), SHOW_DELAY_MS);

    const onMouseLeave = (e) => {
      // Exit-intent: mouse leaves through the top of the viewport
      if (e.clientY <= 0) trigger("exit_intent");
    };
    document.addEventListener("mouseout", onMouseLeave);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseLeave);
    };
  }, []);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const valid = form.first_name.trim().length > 0 && /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!valid) return;
    setSubmitting(true);
    try {
      const ref = getStoredReferrer();
      await createLead({
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        phone: "",
        referred_by: ref || "",
      });
      track("subscribe_modal_submit", { ref: ref || undefined });
      setDone(true);
      toast.success("You're in. Check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't subscribe. Please try the form below.");
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    setOpen(false);
    track("subscribe_modal_dismiss");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent
        data-testid="subscribe-modal"
        className="bg-white border border-[var(--line)] text-ink max-w-md sm:max-w-lg p-0 overflow-hidden"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          data-testid="subscribe-modal-close"
          className="absolute right-4 top-4 z-10 text-ink/45 hover:text-ink p-1 rounded-full"
        >
          <X size={18} />
        </button>

        {done ? (
          <div data-testid="subscribe-modal-success" className="p-8 sm:p-10 text-center space-y-5">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-soft">
              <CheckCircle2 className="text-brand" size={28} />
            </div>
            <div className="space-y-2">
              <div className="font-display text-3xl sm:text-4xl tracking-[-0.025em] leading-tight">
                You&rsquo;re <span className="font-serif-em italic text-brand font-light">in.</span>
              </div>
              <p className="text-ink/70 text-[15px] leading-relaxed">
                Check <strong>{form.email}</strong> &mdash; the details are on their way. (Peek in Spam/Promotions if you don&apos;t see it in a few minutes.)
              </p>
            </div>
            <Button
              type="button"
              onClick={dismiss}
              data-testid="subscribe-modal-close-after"
              className="btn-ink rounded-full h-11 px-6"
            >
              Got it
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-[1fr_1.4fr]">
            {/* Left accent panel — hidden on mobile to keep modal compact */}
            <div className="hidden sm:flex bg-bone border-r border-[var(--line)] p-6 flex-col justify-between">
              <div className="eyebrow !text-brand">Don&rsquo;t miss this</div>
              <div className="font-serif-em italic text-3xl text-ink leading-tight">
                Your idea<br/>has waited<br/>long enough.
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-mono-spec">
                {sc.lab_date_primary_label} · {sc.lab_date_primary_time}
              </div>
            </div>

            <form onSubmit={submit} className="p-6 sm:p-7 space-y-4" data-testid="subscribe-modal-form">
              <div className="flex items-center gap-2 text-brand">
                <Mail size={16} />
                <span className="eyebrow !text-brand !text-[10px]">Want the details?</span>
              </div>
              <div className="space-y-1.5">
                <h3 className="font-display text-2xl sm:text-[28px] tracking-[-0.025em] text-ink leading-[1.1]">
                  Get the AI Build Lab guide <span className="font-serif-em italic text-brand font-light">in your inbox.</span>
                </h3>
                <p className="text-ink/65 text-sm leading-relaxed">
                  Just your name + email &mdash; we&rsquo;ll send you the lab details and a small set of build prompts to get your idea moving. No spam, unsubscribe anytime.
                </p>
              </div>

              <div className="space-y-3 pt-1">
                <div>
                  <Label htmlFor="sm_first" className="text-[10px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">First name</Label>
                  <Input
                    id="sm_first"
                    data-testid="subscribe-modal-first"
                    value={form.first_name}
                    onChange={(e) => update("first_name", e.target.value)}
                    autoComplete="given-name"
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
                <div>
                  <Label htmlFor="sm_email" className="text-[10px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Email</Label>
                  <Input
                    id="sm_email"
                    type="email"
                    data-testid="subscribe-modal-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    autoComplete="email"
                    className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting || !valid}
                data-testid="subscribe-modal-submit"
                className="btn-ink rounded-full h-11 px-6 w-full sm:w-auto"
              >
                {submitting ? <Loader2 className="animate-spin" size={14} /> : "Send me the details"}
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
